import {
    Checkbox,
    FormLabel,
    IdOptionType,
    InputList,
    OptionType,
    SearchableSelect,
    Textinput
} from "shared";
import {
    ITemplateComponent,
    ITemplateSelectComponent
} from "entities/template";
import React, { useEffect, useState } from "react";
import { useComponentsTranslations } from "../../../../lib/i18n";
import { useComponentValidateFunction } from "../../../../lib/hooks/useComponentValidateFunction";
import { VisibilityField } from "../../form_fields/visibility_field/visibility_field";
import { LabelField } from "../../form_fields/label_field/label_field";
import { TooltipField } from "../../form_fields/tooltip_field/tooltip_field";
import styles from "./component_form_select.module.scss";

interface IComponentFormSelectProps {
    component: ITemplateSelectComponent;
    enLanguageEnabled: boolean;
    componentList: ITemplateComponent[];
    triggerValidate: number;
    onChange: (component: ITemplateComponent) => void;
}

export const ComponentFormSelect: React.FC<IComponentFormSelectProps> = React.memo(({
                                                                                        component,
                                                                                        enLanguageEnabled,
                                                                                        componentList,
                                                                                        triggerValidate,
                                                                                        onChange
                                                                                    }) => {
    const t = useComponentsTranslations();
    const { validateTag, validateList } = useComponentValidateFunction();
    const [defaultValueOptions, setDefaultValueOptions] = useState<IdOptionType[]>([]);

    const optionMap = (value: string, id: number) => {
        return {
            value,
            label: value,
            id
        }
    };

    useEffect(() => {
        Array.isArray(component.list) && setDefaultValueOptions(component.list.map(optionMap));
    }, [component.list])


    const onInputListChange = (list: string[], list_en: string[]) => {
        let defaultValue: string | undefined = undefined;

        if (component.defaultValue && list.includes(component.defaultValue)) {
            defaultValue = component.defaultValue;
        }

        onChange({
            ...component,
            list,
            list_en,
            defaultValue
        });
    };


    const onFieldChange = (value: Partial<ITemplateSelectComponent>) => {
        onChange({
            ...component,
            ...value
        })
    };


    return <div className={styles.container}>
        <LabelField component={component}
                    enLanguageEnabled={enLanguageEnabled}
                    title={t("formLabel")}
                    name="label-dynamic-input"
                    onChange={onFieldChange}/>

        <FormLabel title={t("formTag")} required={true}>
            <Textinput size="m"
                       value={component.name ? component.name : ""}
                       triggerValidate={triggerValidate}
                       validate={true}
                       validateFunc={validateTag}
                       onChange={(e) => onFieldChange({ name: e.target.value })}
                       view="outline"/>
        </FormLabel>

        <FormLabel title={t("list")}>
            <InputList
                listId={component.id}
                list={component.list}
                list_en={component.list_en}
                triggerValidate={triggerValidate}
                validate={true}
                validateFunc={validateList}
                enLanguageEnabled={enLanguageEnabled}
                onChange={options => onInputListChange(
                    options.map((o) => o.value),
                    options.map((o) => o.value_en)
                )
                }
            />
        </FormLabel>

        <FormLabel title={t("defaultValue")}>
            <SearchableSelect options={defaultValueOptions}
                              optionValue={defaultValueOptions.find((o) => o.value === component.defaultValue) as OptionType}
                              onChange={(value) => onFieldChange({ defaultValue: value as string })}
                              isMulti={false}
                              hideClearIcon={false}
                              isSearchable={true}/>
        </FormLabel>

        <VisibilityField
            component={component}
            componentList={componentList}
            onChange={onFieldChange}/>

        <TooltipField
            component={component}
            enLanguageEnabled={enLanguageEnabled}
            onChange={onFieldChange}
        />

        <div className={styles.checkboxGroup}>
            <Checkbox
                label={t("requiredField")}
                size="m"
                onChange={() => onFieldChange({ required: typeof component.required === "boolean" ? !component.required : false })}
                checked={typeof component.required === "boolean" ? component.required : true}
            />
            <Checkbox
                label={t("rowGroup")}
                size="m"
                onChange={() => onFieldChange({ isRowGroup: !component.isRowGroup })}
                checked={component.isRowGroup}
            />
        </div>

        <div className={styles.checkboxGroup}>
            <Checkbox
                label={t("readonlyField")}
                size="m"
                onChange={() => onFieldChange({ readonly: !component.readonly })}
                checked={component.readonly}
            />
        </div>

    </div>;
});

